import { getInitials } from '../../utils/format';

interface AvatarProps {
  name: string;
  imageUrl?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

/**
 * Avatar component with fallback to initials.
 */
export function Avatar({ name, imageUrl, size = 'md', className = '' }: AvatarProps) {
  const sizeClasses = {
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-14 h-14 text-lg',
    xl: 'w-20 h-20 text-2xl',
  };

  const initials = getInitials(name);

  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={name}
        className={`
          ${sizeClasses[size]}
          rounded-full object-cover
          ${className}
        `}
      />
    );
  }

  return (
    <div
      className={`
        ${sizeClasses[size]}
        rounded-full bg-primary-600
        flex items-center justify-center
        font-semibold text-white
        ${className}
      `}
    >
      {initials}
    </div>
  );
}

interface AvatarGroupProps {
  items?: AvatarGroupItem[];
  names?: string[];
  max?: number;
  size?: 'sm' | 'md' | 'lg';
}

interface AvatarGroupItem {
  name: string;
  imageUrl?: string;
}

/**
 * Group of overlapping avatars.
 */
export function AvatarGroup({ items, names, max = 4, size = 'md' }: AvatarGroupProps) {
  const resolvedItems: AvatarGroupItem[] =
    items ?? (names ?? []).map((name) => ({ name }));
  const displayed = resolvedItems.slice(0, max);
  const remaining = resolvedItems.length - max;

  const overlapClasses = {
    sm: '-ml-2',
    md: '-ml-3',
    lg: '-ml-4',
  };

  return (
    <div className="flex items-center">
      {displayed.map((item, index) => (
        <Avatar
          key={index}
          name={item.name}
          imageUrl={item.imageUrl}
          size={size}
          className={`
            border-2 border-dark-200
            ${index > 0 ? overlapClasses[size] : ''}
          `}
        />
      ))}

      {remaining > 0 && (
        <div
          className={`
            ${overlapClasses[size]}
            w-10 h-10 rounded-full
            bg-dark-400 border-2 border-dark-200
            flex items-center justify-center
            text-sm text-gray-300
          `}
        >
          +{remaining}
        </div>
      )}
    </div>
  );
}
